import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, ArrowLeftIcon, CheckIcon, DollarSignIcon, TrendingUpIcon, CalendarIcon } from 'lucide-react';
export function MultiStepForm() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    fundingAmount: '',
    purpose: '',
    timeInBusiness: '',
    monthlyRevenue: ''
  });
  const navigate = useNavigate();
  const totalSteps = 5;
  const nextStep = () => setStep(prev => Math.min(prev + 1, totalSteps));
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));
  const progress = step / totalSteps * 100;
  const selectOption = (field: string, value: string) => {
    setFormData({
      ...formData,
      [field]: value
    });
    nextStep();
  };
  return <section id="apply" className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-black" data-id="element-459">
      <div className="max-w-3xl mx-auto" data-id="element-460">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-461">
          <h2 className="text-3xl md:text-4xl font-medium text-white mb-4" data-id="element-462">
            Claim Your Pre-Approval
          </h2>
          <p className="text-zinc-400" data-id="element-463">
            Complete the assessment below to view your funding options.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8" data-id="element-464">
          <div className="flex justify-between text-xs font-medium text-zinc-500 mb-3 uppercase tracking-wider" data-id="element-465">
            <span data-id="element-466">Assessment Progress</span>
            <span data-id="element-467">
              {step} / {totalSteps}
            </span>
          </div>
          <div className="h-1 bg-zinc-900 rounded-full overflow-hidden" data-id="element-468">
            <motion.div className="h-full bg-white" initial={{
            width: 0
          }} animate={{
            width: `${progress}%`
          }} transition={{
            duration: 0.4
          }} data-id="element-469" />
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl overflow-hidden backdrop-blur-md" data-id="element-470">
          <div className="p-8 md:p-12" data-id="element-471">
            <AnimatePresence mode="wait" data-id="element-472">
              {step === 1 && <Step1 key="step1" onSelect={value => selectOption('fundingAmount', value)} data-id="element-473" />}
              {step === 2 && <Step2 key="step2" onSelect={value => selectOption('purpose', value)} onPrev={prevStep} data-id="element-474" />}
              {step === 3 && <Step3 key="step3" onSelect={value => selectOption('timeInBusiness', value)} onPrev={prevStep} data-id="element-475" />}
              {step === 4 && <Step4 key="step4" onSelect={value => selectOption('monthlyRevenue', value)} onPrev={prevStep} data-id="element-476" />}
              {step === 5 && <Step5 key="step5" onPrev={prevStep} formData={formData} navigate={navigate} data-id="element-477" />}
            </AnimatePresence>
          </div>

          {/* Trust Footer */}
          <div className="bg-black/40 border-t border-zinc-800 px-6 py-4 flex items-center justify-center gap-3" data-id="element-478">
            <div className="flex items-center gap-2" data-id="element-479">
              <div className="w-2 h-2 bg-green-500 rounded-full" data-id="element-480"></div>
              <span className="text-xs uppercase tracking-wider text-zinc-500" data-id="element-481">
                256-bit SSL Encrypted
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
// Wrapper Component
const StepWrapper = ({
  children,
  title,
  subtitle,
  onPrev,
  icon: Icon
}: {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  onPrev?: () => void;
  icon?: any;
}) => <motion.div initial={{
  opacity: 0,
  x: 20
}} animate={{
  opacity: 1,
  x: 0
}} exit={{
  opacity: 0,
  x: -20
}} transition={{
  duration: 0.3
}} data-id="element-482">
    <div className="mb-8" data-id="element-483">
      {onPrev && <button onClick={onPrev} className="mb-6 inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm transition-colors" data-id="element-484">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-485" />
          Back
        </button>}

      <div className="flex items-start gap-4" data-id="element-486">
        <div data-id="element-487">
          <h3 className="text-2xl md:text-3xl font-medium text-white mb-2" data-id="element-488">
            {title}
          </h3>
          {subtitle && <p className="text-zinc-400" data-id="element-489">{subtitle}</p>}
        </div>
      </div>
    </div>
    {children}
  </motion.div>;
// Option Button Component
const OptionButton = ({
  onClick,
  label,
  sublabel
}: {
  onClick: () => void;
  label: string;
  sublabel?: string;
}) => <button onClick={onClick} className="w-full p-5 border border-zinc-800 bg-black/40 rounded-xl hover:bg-zinc-800 hover:border-zinc-700 transition-all flex items-center justify-between group text-left" data-id="element-490">
    <div data-id="element-491">
      <span className="block font-medium text-lg text-white group-hover:text-white" data-id="element-492">
        {label}
      </span>
      {sublabel && <span className="block text-sm text-zinc-500 mt-1" data-id="element-493">{sublabel}</span>}
    </div>
    <ArrowRightIcon className="w-5 h-5 text-zinc-600 group-hover:text-white transition-colors" data-id="element-494" />
  </button>;
// Step Components
const Step1 = ({
  onSelect
}: {
  onSelect: (value: string) => void;
}) => <StepWrapper title="Capital Requirements" subtitle="Select your desired funding range." icon={DollarSignIcon} data-id="element-495">
    <div className="space-y-3" data-id="element-496">
      <OptionButton onClick={() => onSelect('25k-50k')} label="$25,000 - $50,000" data-id="element-497" />
      <OptionButton onClick={() => onSelect('50k-100k')} label="$50,000 - $100,000" data-id="element-498" />
      <OptionButton onClick={() => onSelect('100k-250k')} label="$100,000 - $250,000" data-id="element-499" />
      <OptionButton onClick={() => onSelect('250k-500k')} label="$250,000 - $500,000" data-id="element-500" />
      <OptionButton onClick={() => onSelect('500k+')} label="$500,000+" data-id="element-501" />
    </div>
  </StepWrapper>;
const Step2 = ({
  onSelect,
  onPrev
}: {
  onSelect: (value: string) => void;
  onPrev: () => void;
}) => <StepWrapper title="Use of Funds" subtitle="How do you intend to deploy this capital?" onPrev={onPrev} icon={TrendingUpIcon} data-id="element-502">
    <div className="grid md:grid-cols-2 gap-3" data-id="element-503">
      <OptionButton onClick={() => onSelect('working-capital')} label="Working Capital" data-id="element-504" />
      <OptionButton onClick={() => onSelect('equipment')} label="Equipment" data-id="element-505" />
      <OptionButton onClick={() => onSelect('inventory')} label="Inventory" data-id="element-506" />
      <OptionButton onClick={() => onSelect('expansion')} label="Expansion" data-id="element-507" />
      <OptionButton onClick={() => onSelect('payroll')} label="Payroll" data-id="element-508" />
      <OptionButton onClick={() => onSelect('marketing')} label="Marketing" data-id="element-509" />
      <OptionButton onClick={() => onSelect('debt')} label="Refinancing" data-id="element-510" />
      <OptionButton onClick={() => onSelect('other')} label="Other" data-id="element-511" />
    </div>
  </StepWrapper>;
const Step3 = ({
  onSelect,
  onPrev
}: {
  onSelect: (value: string) => void;
  onPrev: () => void;
}) => <StepWrapper title="Business History" subtitle="How long has your entity been operating?" onPrev={onPrev} icon={CalendarIcon} data-id="element-512">
    <div className="space-y-3" data-id="element-513">
      <OptionButton onClick={() => onSelect('0-6months')} label="Less than 6 months" data-id="element-514" />
      <OptionButton onClick={() => onSelect('6-12months')} label="6-12 months" data-id="element-515" />
      <OptionButton onClick={() => onSelect('1-2years')} label="1-2 years" data-id="element-516" />
      <OptionButton onClick={() => onSelect('2-5years')} label="2-5 years" data-id="element-517" />
      <OptionButton onClick={() => onSelect('5+years')} label="5+ years" data-id="element-518" />
    </div>
  </StepWrapper>;
const Step4 = ({
  onSelect,
  onPrev
}: {
  onSelect: (value: string) => void;
  onPrev: () => void;
}) => <StepWrapper title="Monthly Revenue" subtitle="Average monthly gross revenue over the last 3 months." onPrev={onPrev} icon={TrendingUpIcon} data-id="element-519">
    <div className="space-y-3" data-id="element-520">
      <OptionButton onClick={() => onSelect('under-10k')} label="Under $10,000" data-id="element-521" />
      <OptionButton onClick={() => onSelect('10k-25k')} label="$10,000 - $25,000" data-id="element-522" />
      <OptionButton onClick={() => onSelect('25k-50k')} label="$25,000 - $50,000" data-id="element-523" />
      <OptionButton onClick={() => onSelect('50k-100k')} label="$50,000 - $100,000" data-id="element-524" />
      <OptionButton onClick={() => onSelect('100k+')} label="$100,000+" data-id="element-525" />
    </div>
  </StepWrapper>;
const Step5 = ({
  onPrev,
  formData,
  navigate
}: {
  onPrev: () => void;
  formData: any;
  navigate: any;
}) => {
  const handleFinalize = () => {
    navigate('/application');
  };
  return <StepWrapper title="Ready to Proceed" subtitle="Complete your full application to receive your funding options." onPrev={onPrev} icon={CheckIcon} data-id="element-526">
      <div className="text-center py-8" data-id="element-527">
        <button onClick={handleFinalize} className="w-full bg-white text-black font-medium text-lg py-4 rounded-xl hover:bg-gray-200 transition-all flex items-center justify-center gap-3 shadow-lg" data-id="element-528">
          Finalize Your Application
          <ArrowRightIcon className="w-5 h-5" data-id="element-529" />
        </button>

        <p className="text-xs text-zinc-500 mt-6" data-id="element-530">
          Your assessment is complete. Click above to continue to the full
          application.
        </p>
      </div>
    </StepWrapper>;
};